import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import {
  MaximumFractionDigits,
  PrecisionFloat,
  getDecimalsByDisplayType,
  getPrecisionFloatValue,
} from '@bts-web/utils-formatting';
import { AmountFor } from '@bts-web/data-layer/server';
import {
  AmountQuickSelectValuePassed,
  valueFromAssetToFiat,
  valueFromFiatToAsset,
  valueFromAssetToAsset,
} from '../../../../common';
import { validateAmountInput } from '../../../../common/utils/amountInputs/validateAmountInput';
import { MaximumTradeAmountType, MinimumTradeAmountType } from '../../types';
import { TradeControllerVariants } from '../../../utils/reusableTypes';
import { TARGET_PRICE_KEY } from '../../constants';

export type TradeInputErrorType = {
  tooHigh?: boolean;
  tooLow?: boolean;
  customError?: string | null;
};

export const isTooHighOrToLowAmount = ({
  maximumTradeAmount,
  minimumTradeAmount,
  currentTradeAmount,
}: {
  maximumTradeAmount?: MaximumTradeAmountType['ASSET' | 'FIAT'];
  minimumTradeAmount?: MinimumTradeAmountType['ASSET' | 'FIAT'];
  currentTradeAmount: number;
}) => {
  let isTooHigh = false;

  let isTooLow = false;

  const maxTradeValue = Number(getPrecisionFloatValue(maximumTradeAmount));

  const minTradeValue = Number(getPrecisionFloatValue(minimumTradeAmount));

  if (maximumTradeAmount && currentTradeAmount > maxTradeValue) {
    isTooHigh = true;
  }

  if (
    (minimumTradeAmount &&
      currentTradeAmount < minTradeValue &&
      currentTradeAmount > 0) ||
    currentTradeAmount === 0
  ) {
    isTooLow = true;
  }

  return { isTooHigh, isTooLow };
};

export const useTradeInput = ({
  rootMaximumTradeAmount,
  rootMinimumTradeAmount,
  price,
  initialInputValue,
  targetAssetPrice, // ? present only for swaps
  transactionType,
}: {
  rootMaximumTradeAmount: MaximumTradeAmountType;
  rootMinimumTradeAmount: MinimumTradeAmountType;
  price: PrecisionFloat;
  targetAssetPrice?: PrecisionFloat;
  initialInputValue?: string;
  transactionType: TradeControllerVariants;
}) => {
  const searchParams = useSearchParams();

  const isSwapTrade = transactionType === 'swap';

  const isStakeTrade = transactionType === 'stake';

  const isUnstakeTrade = transactionType === 'unstake';

  const isTargetPricePage = searchParams.has(TARGET_PRICE_KEY);

  const isOrderSellTrade =
    transactionType === 'order' &&
    searchParams.has('orderType') &&
    (searchParams.get('orderType') || '') === 'SELL' &&
    !isTargetPricePage;

  const [preferredAssetPrecision, setPreferredAssetPrecision] =
    useState<number>(MaximumFractionDigits.ASSET);

  const [displayType, setDisplayType] = useState<AmountFor>(
    isSwapTrade || isStakeTrade || isUnstakeTrade || isOrderSellTrade
      ? 'ASSET'
      : 'FIAT',
  );
  // ! displayType switcher is currently only displayed for trades of type SELL
  // ! if enabling for swaps, 'FIAT' AND 'ASSET' identifiers would be used both for classic trades and swaps ( where we have 'SOURCE' and 'TARGET') - NEEDS REFACTORING

  const maximumTradeAmount =
    rootMaximumTradeAmount[isSwapTrade ? 'ASSET' : displayType];

  const minimumTradeAmount =
    rootMinimumTradeAmount[isSwapTrade ? 'ASSET' : displayType];

  const [errors, setErrors] = useState<TradeInputErrorType>({
    tooHigh: false,
    tooLow: false,
    customError: null,
  });

  const [inputValue, setInputValue] = useState<string>(initialInputValue ?? '');

  const onQuickSelectButtonClick = (value: AmountQuickSelectValuePassed) => {
    // the quick select => sell buttons must switch the view to ASSET if the user is in FIAT and selling, and bind the value, based on that view, after the change is done
    if (displayType === 'FIAT' && transactionType === 'sell') {
      setDisplayType('ASSET');

      setInputValue(getPrecisionFloatValue(value['ASSET']));
    } else {
      setInputValue(getPrecisionFloatValue(value[displayType]));
    }
  };

  const computeNewValue = (valueType: AmountFor): string => {
    if (targetAssetPrice) {
      return (
        (valueType === 'ASSET'
          ? valueFromAssetToAsset({
              amount: inputValue,
              fromPrice: price,
              toPrice: targetAssetPrice,
              customPrecision: preferredAssetPrecision,
            })
          : valueFromAssetToAsset({
              amount: inputValue,
              fromPrice: targetAssetPrice,
              toPrice: price,
              customPrecision: preferredAssetPrecision,
            })
        )?.value ?? '0'
      );
    }

    return (
      (valueType === 'FIAT'
        ? valueFromAssetToFiat(inputValue, price)
        : valueFromFiatToAsset(inputValue, price, preferredAssetPrecision)
      )?.value ?? '0'
    );
  };

  const onChangeDisplayType = useCallback(
    (newType: AmountFor) => {
      // bind and convert only if there's a number there, otherwise it's filled with zeroes

      if (inputValue.length > 0) {
        let newValue = '';

        const previousDisplayType = newType === 'FIAT' ? 'ASSET' : 'FIAT';

        const wasMax =
          inputValue === rootMaximumTradeAmount[previousDisplayType]?.value;

        // TODO: possible backend calculation bug, for the value of maximum that we receive?
        if (wasMax) {
          newValue = rootMaximumTradeAmount[newType]?.value as string;
        } else {
          newValue = computeNewValue(newType);
        }

        setInputValue(newValue ?? '');
      }

      setDisplayType(newType);
    },
    [
      inputValue,
      preferredAssetPrecision,
      price,
      rootMaximumTradeAmount,
      targetAssetPrice,
    ],
  );

  const onTradeInputChange = (newValue: string) => {
    let isValid = false;

    if (targetAssetPrice) {
      isValid = validateAmountInput(
        newValue,
        displayType,
        MaximumFractionDigits.ASSET,
        MaximumFractionDigits.ASSET,
      );
    } else {
      isValid = validateAmountInput(newValue, displayType);
    }

    if (!isValid) return;

    setInputValue(newValue);
  };

  const onInputBlur = (_value: string) => {
    return;
  };

  // Error binding and parsing
  useEffect(() => {
    if (!inputValue) {
      setErrors((state) => ({
        ...state,
        tooHigh: false,
        tooLow: false,
      }));

      return;
    }

    const cleanedNumberDecimalsPreserved = Number(
      inputValue.replace(/[^0-9]/g, '.'),
    );

    // preserve the user's preferred decimal if expressed
    if (displayType === 'ASSET' && cleanedNumberDecimalsPreserved > 0) {
      // user might bind a custom precision when using asset view
      const parts = cleanedNumberDecimalsPreserved.toString().split('.');

      const precision = parts[1]
        ? parts[1].length
        : MaximumFractionDigits.ASSET;

      setPreferredAssetPrecision(precision);
    }

    // do not consider minimum and maximum amounts on target prices
    if (isTargetPricePage) {
      return;
    }

    const decimalsByDisplayType = targetAssetPrice
      ? MaximumFractionDigits.ASSET
      : getDecimalsByDisplayType(displayType);

    // replace anything not a number or a dot with a dot
    const currentTradeAmount = Number(
      cleanedNumberDecimalsPreserved.toFixed(decimalsByDisplayType),
    );

    const { isTooHigh, isTooLow } = isTooHighOrToLowAmount({
      maximumTradeAmount,
      minimumTradeAmount,
      currentTradeAmount,
    });

    setErrors((state) => ({
      ...state,
      tooHigh: isTooHigh,
      tooLow: isTooLow,
    }));
  }, [inputValue, maximumTradeAmount, minimumTradeAmount, displayType]);

  return {
    onTradeInputChange,
    onQuickSelectButtonClick,
    minimumTradeAmount,
    displayType,
    setErrors,
    errors,
    inputValue,
    onInputBlur,
    onChangeDisplayType,
  };
};
